// Ubicación en el repo: src/pages/Prescriptions.jsx
// Listado de Recetas de lentes (OFT) — Fase 1 (CRUD) + Fase 2 (vista de
// detalle "Ver receta") del módulo (ver DECISIONS.md). Mismo patrón que
// Exams.jsx: tabla + búsqueda + filtro por sucursal activa (RLS ya
// restringe por patient_branch_match, esto es solo para acotar a la
// sucursal elegida en el header) + crear/editar vía modal + soft-delete.
// La impresión se agrega en la fase siguiente.
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { listPrescriptions, getPrescriptionById, softDeletePrescription } from '../lib/prescriptions';
import { listActiveProfessionals } from '../lib/professionals';
import { isModuleEnabledForBranch } from '../lib/branches';
import { formatPatientName, formatProfessionalName, formatDate } from '../lib/format';
import PrescriptionFormModal from '../components/PrescriptionFormModal';
import PrescriptionDetailModal from '../components/PrescriptionDetailModal';
import { printPrescriptionReport } from '../lib/prescriptionReport';
import { getPatientBranchLogoUrl } from '../lib/branchLogos';
import { getProfessionalSignatureUrl } from '../lib/professionalSignatures';
import Spinner from '../components/Spinner';

export default function Prescriptions() {
  const { activeBranchId, activeBranch, professional } = useOutletContext();

  const [search, setSearch] = useState('');
  const [prescriptions, setPrescriptions] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const [detailTarget, setDetailTarget] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    setError('');
    try {
      const data = await listPrescriptions({ branchId: activeBranchId, search });
      setPrescriptions(data);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el listado de recetas.');
    } finally {
      setLoading(false);
    }
  }, [activeBranchId, search]);

  useEffect(() => {
    const t = setTimeout(refresh, 300);
    return () => clearTimeout(t);
  }, [refresh]);

  useEffect(() => {
    listActiveProfessionals().then(setProfessionals).catch(() => setProfessionals([]));
  }, []);

  function handleNew() {
    setEditTarget(null);
    setFormOpen(true);
  }

  // La fila del listado no trae los campos clínicos (lejos/cerca/av/...) —
  // solo lo necesario para mostrar la tabla. Para editar o ver el detalle
  // se pide la ficha completa, y se le agregan los datos de
  // paciente/profesional ya embebidos en la fila del listado (evita una
  // segunda consulta para eso). Mismo patrón que loadFullExam en Exams.jsx.
  async function loadFullPrescription(rx) {
    const full = await getPrescriptionById(rx.id);
    return { ...full, patients: rx.patients, professionals: rx.professionals };
  }

  async function handleEditClick(rx) {
    setEditLoading(true);
    setError('');
    try {
      setEditTarget(await loadFullPrescription(rx));
      setFormOpen(true);
    } catch (err) {
      setError(err.message || 'No se pudo cargar la receta.');
    } finally {
      setEditLoading(false);
    }
  }

  async function handleViewClick(rx) {
    setDetailLoading(true);
    setError('');
    try {
      setDetailTarget(await loadFullPrescription(rx));
    } catch (err) {
      setError(err.message || 'No se pudo cargar la receta.');
    } finally {
      setDetailLoading(false);
    }
  }

  function handleEditFromDetail() {
    setEditTarget(detailTarget);
    setDetailTarget(null);
    setFormOpen(true);
  }

  async function handleDeleteClick(rx) {
    const ok = window.confirm(`¿Eliminar la receta de ${formatPatientName(rx.patients)} del ${formatDate(rx.date)}?`);
    if (!ok) return;
    try {
      await softDeletePrescription(rx.id, professional.id);
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la receta.');
    }
  }

  function handleFormSaved() {
    setFormOpen(false);
    setEditTarget(null);
    refresh();
  }

  // Guard de UX (DECISIONS.md #42) — ver mismo comentario en Exams.jsx.
  if (activeBranch && !isModuleEnabledForBranch(activeBranch, 'OFT')) {
    return (
      <div className="patients-page">
        <h1>Recetas de lentes</h1>
        <p className="text-muted">El módulo OFT está deshabilitado para la sucursal activa ({activeBranch.name}).</p>
      </div>
    );
  }

  return (
    <div className="patients-page">
      <div className="patients-page__toolbar">
        <h1>Recetas de lentes</h1>
        <div className="patients-page__toolbar-right">
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por paciente o RUT…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="button-primary" onClick={handleNew}>
            + Nueva receta
          </button>
        </div>
      </div>

      {error && <p className="auth-error">{error}</p>}

      <table className="patients-table">
        <thead>
          <tr>
            <th>Paciente</th>
            <th>Fecha</th>
            <th>Profesional</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5}><Spinner inline /></td>
            </tr>
          ) : prescriptions.length === 0 ? (
            <tr>
              <td colSpan={5}>No hay recetas registradas.</td>
            </tr>
          ) : (
            prescriptions.map((rx) => (
              <tr key={rx.id}>
                <td>
                  <div>{formatPatientName(rx.patients)}</div>
                  <div className="text-muted">{rx.patients?.rut || 'Sin RUT'}</div>
                </td>
                <td>{formatDate(rx.date)}</td>
                <td>{formatProfessionalName(rx.professionals, { withRole: true })}</td>
                <td>{rx.reevaluar ? <span className="badge badge-warn">Reevaluar</span> : '—'}</td>
                <td className="actions-cell">
                  <button type="button" className="link-button" disabled={detailLoading} onClick={() => handleViewClick(rx)}>
                    Ver receta
                  </button>
                  <button type="button" className="link-button" disabled={editLoading} onClick={() => handleEditClick(rx)}>
                    Editar
                  </button>
                  <button type="button" className="link-button link-button--danger" onClick={() => handleDeleteClick(rx)}>
                    Eliminar
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {formOpen && (
        <PrescriptionFormModal
          prescription={editTarget}
          branchId={activeBranchId}
          professionals={professionals}
          actorId={professional.id}
          onClose={() => {
            setFormOpen(false);
            setEditTarget(null);
          }}
          onSaved={handleFormSaved}
        />
      )}

      {detailTarget && (
        <PrescriptionDetailModal
          prescription={detailTarget}
          onClose={() => setDetailTarget(null)}
          onEdit={handleEditFromDetail}
          onPrint={async () => {
            const [logoUrl, signatureUrl] = await Promise.all([
              getPatientBranchLogoUrl(detailTarget.patients, activeBranchId),
              getProfessionalSignatureUrl(detailTarget.professionals),
            ]);
            printPrescriptionReport(detailTarget, logoUrl, signatureUrl);
          }}
        />
      )}
    </div>
  );
}
